import type * as React from "react"
/**
 * Orders a page's bands by the `section_order` prop — the registry entry ids
 * in display order, as arranged in the admin (PageSectionOrder). Each band
 * names the registry entry it renders; bands the list doesn't mention keep
 * their place after the listed ones, so a registry change can't lose one.
 */
export function orderBands<T extends { id: string }>(bands: T[], order?: string[]): T[] {
  if (!order || order.length === 0) return bands
  const position = (id: string) => {
    const i = order.indexOf(id)
    return i === -1 ? order.length : i
  }
  return bands
    .map((band, index) => ({ band, index }))
    .sort((a, b) => position(a.band.id) - position(b.band.id) || a.index - b.index)
    .map(({ band }) => band)
}

/** A page block that can be moved from the admin: the registry entry id it
 *  corresponds to, whether it has anything to show, and how to render it at
 *  a given position (the index drives the background alternation). */
export type PageBand = {
  id: string
  present: boolean
  render: (index: number) => React.ReactNode
}

/** The bands to draw, in the admin's order, with the empty ones dropped. */
export function presentBands(bands: PageBand[], order?: string[]): PageBand[] {
  return orderBands(bands, order).filter((band) => band.present)
}
